import ChartHeader from '../ChartHeader';
import NoResponsesAvailableYet from '../NoResponsesAvailableYet';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Box } from '@mui/material';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

//See https://www.nudgebi.com/color-library
const positiveColors = ['#4E93AE', '#336F9C', '#1B4D88', '#163063', '#111842', '#0C0F24'];
const negativeColors = ['#F3A452', '#D77B30', '#B45B23', '#913D1A', '#6D2312', '#4D0507'];

const ResponseDistributionChart = ({ data = [], onRefresh, isFirstLoad }) => {
  const chartRef = useRef(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [calculatedLegendWidth, setCalculatedLegendWidth] = useState();

  const calculatePercentage = () => {
    const screenWidth = window.innerWidth;

    setCalculatedLegendWidth(screenWidth - 150);
  };

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setIsManualRefreshing(false), 500);
  };

  // Filter only closed questions (exclude open-ended)
  const closedQuestions = (data || []).filter((q) =>
    ['multiplechoice', 'singlechoice', 'ranking'].includes(q.questionType)
  );

  // Function to group questions by their option patterns
  const groupQuestionsByOptions = (questions) => {
    const groupedQuestions = new Map();
    const noScaleQuestions = [];

    for (const question of questions) {
      // Check if question has no responses or empty responses
      if (!question.responses || question.responses.length === 0) {
        noScaleQuestions.push(question);
      } else {
        const options = question?.responses.map((r) => r.option.trim());

        // Check if all options are empty or invalid
        if (options.every((opt) => !opt)) {
          noScaleQuestions.push(question);
        } else {
          const key = options.join('|');

          if (!groupedQuestions.has(key)) {
            groupedQuestions.set(key, {
              options: options,
              questions: [],
              groupName: generateDynamicGroupName(options)
            });
          }

          groupedQuestions.get(key).questions.push(question);
        }
      }
    }

    // Convert map to array (all valid groups first)
    const result = Array.from(groupedQuestions.values());

    // Add "No Response Scale" group at the end if there are questions without proper responses
    if (noScaleQuestions.length > 0) {
      result.push({
        options: [],
        questions: noScaleQuestions,
        groupName: 'No Response Scale'
      });
    }

    return result;
  };

  // Function to generate dynamic group names based on actual options
  const generateDynamicGroupName = (options) => {
    if (!options || options.length === 0 || options.every((opt) => !opt.trim())) {
      return 'No Response Scale';
    }

    return '';
  };

  useEffect(() => {
    // Calculate initially when the component mounts
    calculatePercentage();

    // Add event listener to recalculate when the window is resized
    window.addEventListener('resize', calculatePercentage);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', calculatePercentage);
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // Function to create the chart
  useLayoutEffect(() => {
    if (!closedQuestions || closedQuestions?.length === 0) return;

    // Get grouped questions
    const questionGroups = groupQuestionsByOptions(closedQuestions);

    // Calculate total number of options across all groups
    const totalOptions = questionGroups?.reduce((sum, group) => sum + (group.options?.length || 0), 0);

    // Calculate dynamic height based on number of items
    const totalQuestions = closedQuestions.length;
    const groupHeaders = questionGroups.length;
    const totalItems = totalQuestions + groupHeaders;
    const calculatedHeight = totalOptions * 20 + totalItems * 50 + 50; // Extra 50px for padding

    // Set the chart container height
    if (chartRef.current) {
      chartRef.current.style.height = `${calculatedHeight}px`;
    }

    // Create root
    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo.dispose();

    // Create main container with horizontal layout
    const mainContainer = root.container.children.push(
      am5.Container.new(root, {
        layout: root.horizontalLayout,
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    // Create chart container
    const chartContainer = mainContainer.children.push(
      am5.Container.new(root, {
        layout: root.verticalLayout,
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    // Create legend container for groups
    const legendsContainer = mainContainer.children.push(
      am5.Container.new(root, {
        layout: root.verticalLayout,
        width: am5.percent(30),
        height: am5.percent(100),
        paddingLeft: 20
      })
    );

    // Create chart
    const chart = chartContainer.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 0
      })
    );

    // Prepare chart data with groups and questions
    let chartData = [];
    const allUniqueOptions = [
      ...new Set(
        closedQuestions?.flatMap(
          (q) => q?.responses?.map((r) => r?.option?.trim())?.filter((opt) => opt) || []
        )
      )
    ];

    // Track group positions for legend alignment
    let groupPositions = [];
    let currentPosition = 0;

    questionGroups.forEach((group) => {
      const isNoScaleGroup = group.groupName === 'No Response Scale';

      // Store group start position
      const groupStartPosition = currentPosition;

      chartData.push({
        question: ``,
        isGroupHeader: true,
        isNoScaleGroup: isNoScaleGroup,
        groupName: group.groupName
      });
      currentPosition++;

      // Add questions in this group
      group.questions.forEach((question) => {
        // Filter responses to only include non-zero values
        const filteredResponses = (question.responses || []).filter((r) => r.count > 0 || r.percentage > 0);

        // Get only options that have responses
        const activeOptions = filteredResponses.map((r) => r.option.trim()).filter((opt) => opt);

        const questionData = {
          question: question?.questionText || '',
          questionFull: question.questionText,
          groupName: group.groupName,
          isGroupHeader: false,
          isGroupSeparator: false,
          isNoScaleGroup: isNoScaleGroup
        };

        if (!isNoScaleGroup) {
          // Only initialize active options (those with responses)
          activeOptions.forEach((option) => {
            const key = option.toLowerCase().replace(/[^a-z0-9]/g, '_');
            questionData[key] = 0;
            questionData[`${key}_count`] = 0;
          });

          filteredResponses.forEach((response) => {
            if (response.option && response.option.trim()) {
              const key = response.option
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '_');
              // Only set data if this option exists in the current group
              if (activeOptions.includes(response.option.trim())) {
                questionData[key] = response.percentage || 0;
                questionData[`${key}_count`] = response.count || 0;
              }
            }
          });
        } else {
          questionData['no_scale_indicator'] = 100;
          questionData['no_scale_indicator_count'] = question.totalResponses || 0;
        }

        chartData.push(questionData);
        currentPosition++;
      });

      // Store group info for legend positioning
      groupPositions.push({
        group: group,
        startPosition: groupStartPosition,
        questionCount: group.questions.length + 1, // +1 for header
        isNoScale: isNoScaleGroup
      });
    });

    // REVERSE groupPositions to match bottom-to-top chart rendering
    groupPositions = groupPositions.reverse();

    // Recalculate startPosition for each group after reversing
    groupPositions.forEach((groupInfo, index) => {
      // Calculate the new visual position from top
      // Sum of all previous groups' question counts
      let cumulativePosition = 0;
      for (let i = 0; i < index; i++) {
        cumulativePosition += groupPositions[i].questionCount;
      }
      groupInfo.visualStartPosition = cumulativePosition;
    });

    // Create axes - swap X and Y for horizontal bars
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'question',
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 10,
          cellStartLocation: 0.1
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    // Keep your existing Y-axis styling
    yAxis.get('renderer').labels.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const data = dataItem.dataContext;
        if (data?.isGroupHeader) {
          return am5.color(data?.isNoScaleGroup ? '#f44336' : '#1976d2');
        } else if (data?.isGroupSeparator) {
          return am5.color('#000000');
        } else if (data?.isNoScaleGroup) {
          return am5.color('#757575');
        }
      }
      return am5.color('#333333');
    });

    yAxis.get('renderer').labels.template.adapters.add('fontWeight', (fontWeight, target) => {
      const dataItem = target.dataItem;
      return dataItem?.dataContext?.isGroupHeader ? 'bold' : 'normal';
    });

    yAxis.get('renderer').labels.template.adapters.add('fontSize', (fontSize, target) => {
      const dataItem = target.dataItem;
      if (dataItem?.dataContext?.isGroupHeader) {
        return 13;
      } else if (dataItem?.dataContext?.isGroupSeparator) {
        return 13;
      }
      return 14;
    });

    yAxis.get('renderer').labels.template.setAll({
      maxWidth: 650,
      oversizedBehavior: 'wrap',
      textAlign: 'right',
      paddingRight: 20
    });

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        renderer: am5xy.AxisRendererX.new(root, {})
      })
    );

    // Hide grid and axis
    yAxis.get('renderer').grid.template.set('forceHidden', true);
    xAxis.get('renderer').grid.template.set('forceHidden', true);
    xAxis.set('visible', false);

    const calculateGroupColors = (options) => {
      const colors = [];
      const numOptions = options.length;
      const middleIndex = Math.floor(numOptions / 2);

      for (let i = 0; i < numOptions; i++) {
        if (i < middleIndex) {
          // Left side: negative colors
          const negativeIndex = middleIndex - 1 - i;
          colors.push(negativeColors[negativeIndex % negativeColors.length]);
        } else if (i === middleIndex) {
          // Middle: neutral positive
          colors.push(positiveColors[0]);
        } else {
          // Right side: positive colors
          const positiveIndex = i - middleIndex;
          colors.push(positiveColors[positiveIndex % positiveColors.length]);
        }
      }

      return colors;
    };

    // Create color mapping
    const responseColors = {};
    questionGroups.forEach((group) => {
      const groupColors = calculateGroupColors(group.options);
      group.options.forEach((option, index) => {
        responseColors[option.trim()] = groupColors[index];
      });
    });

    // Create series
    const seriesMap = {};
    const groupSeriesMap = {}; // Track series by group

    // Add "No Scale" indicator series
    const hasNoScaleQuestions = questionGroups.some((group) => group.groupName === 'No Response Scale');
    if (hasNoScaleQuestions) {
      const noScaleSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'No Response Data',
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: 'no_scale_indicator',
          categoryYField: 'question',
          stacked: true,
          tooltip: am5.Tooltip.new(root, {
            labelText: 'No response options available',
            getFillFromSprite: false,
            autoTextColor: false
          })
        })
      );

      // Set tooltip styling for no scale series
      noScaleSeries
        .get('tooltip')
        .get('background')
        .setAll({
          fill: am5.color('#000000'),
          fillOpacity: 0.8
        });

      noScaleSeries.get('tooltip').label.setAll({
        fill: am5.color('#ffffff')
      });

      noScaleSeries.columns.template.setAll({
        fill: am5.color('#e0e0e0'),
        stroke: am5.color('#e0e0e0'),
        strokeWidth: 1,
        height: 0,
        tooltipText: 'No response options available',
        cursorOverStyle: 'pointer'
      });

      seriesMap['No Response Data'] = noScaleSeries;
    }

    // Create series for regular response options and organize by group
    questionGroups.forEach((group) => {
      if (group.groupName !== 'No Response Scale') {
        groupSeriesMap[group.groupName] = [];

        group.options.forEach((option) => {
          if (allUniqueOptions.includes(option)) {
            const key = option.toLowerCase().replace(/[^a-z0-9]/g, '_');

            const series = chart.series.push(
              am5xy.ColumnSeries.new(root, {
                name: option,
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: key,
                categoryYField: 'question',
                stacked: true,
                tooltip: am5.Tooltip.new(root, {
                  labelText: '{name}: {valueX}% ({' + key + '_count} responses)',
                  getFillFromSprite: false,
                  autoTextColor: false
                })
              })
            );

            // Set tooltip text color to white for better visibility
            series
              .get('tooltip')
              .get('background')
              .setAll({
                fill: am5.color('#000000'),
                fillOpacity: 0.8
              });

            series.get('tooltip').label.setAll({
              fill: am5.color('#ffffff')
            });

            series.columns.template.setAll({
              fill: am5.color(responseColors[option] || positiveColors[0]),
              stroke: am5.color('#ffffff'),
              strokeWidth: 1,
              height: 35,
              tooltipText: '{name}: {valueX}% ({' + key + '_count} responses)',
              cursorOverStyle: 'pointer'
            });

            // Add data label bullets showing count
            series.bullets.push(() => {
              const bullet = am5.Bullet.new(root, {
                locationX: 0.5,
                sprite: am5.Label.new(root, {
                  text: '{' + key + '_count}',
                  fill: am5.color('#ffffff'),
                  centerX: am5.p50,
                  centerY: am5.p50,
                  fontSize: 12,
                  fontWeight: 'bold',
                  populateText: true
                })
              });

              bullet.adapters.add('forceHidden', (forceHidden, target) => {
                const dataItem = target.dataItem;
                if (dataItem) {
                  const data = dataItem.dataContext;
                  return (
                    data.isGroupHeader ||
                    data.isGroupSeparator ||
                    data.isNoScaleGroup ||
                    dataItem.get('valueX') === 0 //Hide if value is zero
                  );
                }
                return false;
              });

              return bullet;
            });

            seriesMap[option] = series;
            groupSeriesMap[group.groupName].push(series);
          }
        });
      }
    });

    // Set data for all series
    Object.values(seriesMap).forEach((series) => {
      series.data.setAll(chartData);
    });
    yAxis.data.setAll(chartData);

    // Create group-specific legends positioned to align with groups
    groupPositions.forEach((groupInfo, index) => {
      if (!groupInfo.isNoScale) {
        // Calculate the CENTER position of this group
        const groupCenterPosition = groupInfo.visualStartPosition + groupInfo.questionCount / 2;
        const groupCenterPercentage = (groupCenterPosition / totalItems) * 100;

        const containerSettings = {
          layout: root.verticalLayout,
          width: am5.percent(100)
          // Remove marginBottom - it interferes with positioning
        };

        // Position legend to align with the CENTER of its chart group
        containerSettings.y = am5.percent(groupCenterPercentage);
        containerSettings.centerY = am5.percent(85); // Center vertically on the position

        // Create a container for this group's legend
        const groupLegendContainer = legendsContainer.children.push(
          am5.Container.new(root, containerSettings)
        );

        // Add group title if it exists
        if (groupInfo.group.groupName) {
          groupLegendContainer.children.push(
            am5.Label.new(root, {
              text: groupInfo.group.groupName,
              fontSize: 13,
              fontWeight: 'bold',
              fill: am5.color('#1976d2'),
              marginBottom: 8
            })
          );
        }

        // Create legend for this group
        const groupLegend = groupLegendContainer.children.push(
          am5.Legend.new(root, {
            layout: root.verticalLayout,
            width: am5.percent(100)
          })
        );

        // Filter to only include series that have this group's specific options
        const relevantSeries = [];
        groupInfo.group.options.forEach((optionName) => {
          if (seriesMap[optionName]) {
            relevantSeries.push(seriesMap[optionName]);
          }
        });

        // Set legend data to only this group's relevant series
        groupLegend.data.setAll(relevantSeries);

        // Get the actual width of the chart container
        const containerWidth = calculatedLegendWidth || 1200;

        // Calculate legend container width (30% of total)
        const legendContainerWidth = containerWidth * 0.3;

        // Calculate available width for text (85% of legend container, minus marker width and padding)
        const dynamicMaxWidth = Math.max(180, legendContainerWidth * 0.85 - 70); // 70 is marker width

        // Use dynamicMaxWidth in legend labels
        groupLegend.labels.template.setAll({
          fontSize: 14,
          maxWidth: dynamicMaxWidth > 495 ? dynamicMaxWidth - 25 : dynamicMaxWidth,
          oversizedBehavior: 'truncate',
          textAlign: 'left',
          cursorOverStyle: 'pointer'
        });

        // Add tooltip to show full text on hover
        groupLegend.labels.template.set(
          'tooltip',
          am5.Tooltip.new(root, {
            getFillFromSprite: false,
            autoTextColor: false,
            pointerOrientation: 'left'
          })
        );

        groupLegend.labels.template
          .get('tooltip')
          .get('background')
          .setAll({
            fill: am5.color('#333333'),
            fillOpacity: 0.95,
            stroke: am5.color('#666666'),
            strokeWidth: 1
          });

        groupLegend.labels.template.get('tooltip').label.setAll({
          fill: am5.color('#ffffff'),
          fontSize: 13,
          maxWidth: 500,
          oversizedBehavior: 'wrap',
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 12,
          paddingRight: 12
        });

        groupLegend.labels.template.adapters.add('tooltipText', (text, target) => {
          const dataItem = target.dataItem;
          if (dataItem && dataItem.dataContext) {
            return dataItem.dataContext.name || text;
          }
          return text;
        });

        // Ensure legend markers match the series colors
        groupLegend.markers.template.setAll({
          width: 16,
          height: 16
        });

        // Add spacing between legend items
        groupLegend.itemContainers.template.setAll({
          paddingTop: 4,
          paddingBottom: 4
        });
      }
    });

    if (isFirstLoad) {
      chart.series.each((series) => series.appear(1000));
      chart.appear(1000, 100);
    }

    return () => root.dispose();
  }, [closedQuestions, isFirstLoad]);

  return (
    <Box>
      <ChartHeader
        title='Response distribution by question groups'
        description='This chart groups survey questions and shows how responses are distributed for each one.'
        showRefreshButton={closedQuestions && closedQuestions.length > 0}
        handleManualRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
      />

      {!closedQuestions || closedQuestions?.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <NoResponsesAvailableYet data={closedQuestions} onRefresh={onRefresh} />
        </Box>
      ) : (
        <Box>
          <Box ref={chartRef} style={{ width: '100%', minHeight: '500px', height: 'auto' }} />

          {/* Add the spinning animation CSS */}
          <style jsx>{`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </Box>
      )}
    </Box>
  );
};

export default ResponseDistributionChart;
